import { DomUtils } from "../utils/domUtils";
import { ExternalProviders } from "../components/externalProviders";
import { LocationUtils } from "../utils/locationUtils";
import { ValidatedInput } from "../components/validatedInput";
import { BaseController } from "./base";

export const LoginConstants = {
	mainView: "mainView",
	antiForgery: "loginAntiForgeryToken",
	loginDescription: "loginDescription",
	loginForm: "loginForm",
	loginFlowContainer: "loginFlowContainer",
	simpleSignOnLabel: "simpleSignOnLabel",
	sendCodeFlowContainer: "sendCodeFlowContainer",
	loginButton: "loginButton",
	sendCodeFlowToggleButton: "sendCodeFlowToggleButton",
	loginFlowToggleButton: "loginFlowToggleButton",
	sendCodeToggleContainer: "sendCodeToggleContainer",
	recaptchaUrl: "https://www.google.com/recaptcha/api.js?render=",
	externalProvidersOrSignInWith: "externalProvidersOrSignInWith",
	externalProvidersContainer: "externalProvidersContainer",
	forgotPassword: "forgotPassword",
	signUp: "signUp",
	codeFlowEmailContainer: "codeFlowEmailContainer",
	codeFlowTextContainer: "codeFlowTextContainer",
	codeFlowModeRadioEmail: "codeFlowModeRadioEmail",
	codeFlowModeRadioText: "codeFlowModeRadioText",
	codeFlowEmail: "codeFlowEmail",
	codeFlowText: "codeFlowText",
	username: "username",
	password: "password",
	sendCodeButton: "sendCodeButton",
	codeFlowUsername: "codeFlowUsername",
	authenticationFlowType: "authenticationFlowType",
	codeFlowRecaptcha: "codeFlowRecaptcha",
	rememberMeCheckClass: "remember-me-check"
};

export const AuthenticationFlowType = Object.freeze({
	Login: 0,
	SendCode: 1
});

export const AuthenticationCodeType = Object.freeze({
	Email: 0,
	Text: 1
});

export class LoginController extends BaseController {
	constructor() {
		super();

		this._signIn = null;
		this._message = null;
		this._antiForgeryToken = null;
		this._loginUrl = null;
		this._signUpLink = null;
		this._forgotPasswordLink = null;
		this._isSendCodeFlow = false;
		this._isCodeFlowEmail = false;
		this._codeFlowUserName = null;
		this._isSimpleSignOn = false;
		this._isAuthCodeEnabled = false;
		this._hasSMSConfigured = false;
		this._domainPhoneMask = null;

		this._recaptchaSiteKey = null;

		this._loginHint = null;
	}

	Init() {
		this._GetContextData();

		this._SetAntiForgeryToken(this._antiForgeryToken);
		this._ShowMessage(this._message);

		this._SetupSimpleSignOnLabel(this._isSimpleSignOn);
		this._SetFormAction(this._loginUrl);
		this._ToggleFlow(this._isSendCodeFlow);

		this._ToggleSendCodeContainer(this._isAuthCodeEnabled);

		if (this._isSendCodeFlow === true) {
			this._ToggleCodeFlowMode(this._isCodeFlowEmail, true);
		}

		this._SetupExternalProviders(this._externalProviders);

		this._SetAccountLinks();
		this._SetupPhoneMask(LoginConstants.codeFlowText, this._domainPhoneMask);

		this._SetupValidatedInputs();
		this._BindEvents();

		const hasErrorMessage = this._message != null && this._message.length > 0;
		const leavingThisPage = this._AutoProceedWithLoginHint(this._isAuthCodeEnabled, this._loginHint, hasErrorMessage);
		if (leavingThisPage === false) {
			this._ShowPage();
		}
	}

	_ShowPage() {
		const mainViewElement = DomUtils.GetElement(LoginConstants.mainView);
		mainViewElement.style.visibility = "visible";
	}

	_AutoProceedWithLoginHint(isAuthCodeEnabled, loginHint, hasErrorMessage) {
		let proceed = false;

		if (hasErrorMessage === false && isAuthCodeEnabled === true && loginHint != null && loginHint.length > 0) {
			const codeFlowEmailElement = DomUtils.GetElement(LoginConstants.codeFlowEmail);
			const codeFlowTextElement = DomUtils.GetElement(LoginConstants.codeFlowText);

			const isPhoneRegex = /^[0-9\-() ]{10,20}$/gi;

			if (loginHint.includes("@")) {
				const trimmedEmail = loginHint.trim();
				codeFlowEmailElement.value = trimmedEmail;
				this._OnCodeFlowModeClicked(true);
				proceed = true;
			}
			else if (this._hasSMSConfigured === true && isPhoneRegex.test(loginHint) === true) {
				const trimmedPhone = loginHint.replace(/[^0-9]/g, "");
				codeFlowTextElement.value = trimmedPhone;
				this._OnCodeFlowModeClicked(false);
				proceed = true;
			}

			if (proceed === true) {
				this._ToggleRememberMe(true);
				this._OnFlowToggleClicked(true);
				this._OnSendCodeButtonClicked();
			}
		}

		if (isAuthCodeEnabled === false) {
			this._ToggleFlow(false);
			this._ToggleSendCodeContainer(false);
		}

		return proceed;
	}

	_GetContextData() {
		this._signIn = LocationUtils.GetQueryParam("signin");

		if (!!window.viewModel) {
			this._externalProviders = window.viewModel.ExternalProviders;

			this._message = LocationUtils.GetQueryParam("message") || window.viewModel.ErrorMessage;
			this._antiForgeryToken = window.viewModel.AntiForgery;
			this._loginUrl = window.viewModel.LoginUrl;

			if (!!window.viewModel.AdditionalLinks && !!window.viewModel.AdditionalLinks.length) {
				var signUpUrl = window.viewModel.AdditionalLinks[0];
				var forgotPasswordUrl = window.viewModel.AdditionalLinks[1];

				if (!!signUpUrl) {
					this._signUpLink = signUpUrl.Href;
				}

				if (!!forgotPasswordUrl) {
					this._forgotPasswordLink = forgotPasswordUrl.Href;
				}
			}

			this._isSendCodeFlow = window.viewModel.AuthenticationType === 1;
			this._isCodeFlowEmail = window.viewModel.AuthenticationCodeType === 0;

			if (this._isSendCodeFlow === true) {
				this._codeFlowUserName = window.viewModel.Username;
			}

			this._loginHint = window.viewModel.AuthenticationType === 1 ? window.viewModel.LoginHint : null;
		}

		if (!!window.domainProperties) {
			this._isSimpleSignOn = !!window.domainProperties.IsSimpleSignOnEnabled;
			this._isAuthCodeEnabled = !!window.domainProperties.IsAuthCodeEnabled;
			this._hasSMSConfigured = !!window.domainProperties.HasSMSConfigured;
			this._domainPhoneMask = window.domainProperties.PhoneMask;
		}

		this._recaptchaSiteKey = window.recaptchaSiteKey || null;
	}

	_SetAntiForgeryToken(token) {
		if (token != null) {
			const antiForgeryHiddenInput = DomUtils.GetElement(LoginConstants.antiForgery);
			if (antiForgeryHiddenInput) {
				antiForgeryHiddenInput.name = token.Name;
				antiForgeryHiddenInput.value = token.Value;
			}
		}
	}

	_SetupSimpleSignOnLabel(isSimpleSignOn) {
		if (isSimpleSignOn === false) {
			this._localeService.SetElementTranslation(LoginConstants.simpleSignOnLabel, "oauth.usernameOrEmail");
		}
	}

	_SetFormAction(loginUrl) {
		const formElement = DomUtils.GetElement(LoginConstants.loginForm);
		formElement.action = loginUrl;
	}

	_SetupValidatedInputs() {
		ValidatedInput.Init(LoginConstants.username);
		ValidatedInput.Init(LoginConstants.password);
		ValidatedInput.Init(LoginConstants.codeFlowEmail);
		ValidatedInput.Init(LoginConstants.codeFlowText);
	}

	_BindEvents() {
		this._BindLogInButton();
		this._BindFlowToggle();
		this._BindEnterKey();
		this._BindCodeFlowModes();
		this._BindSendCodeButton();
	}

	_BindLogInButton() {
		const logInButton = DomUtils.GetElement(LoginConstants.loginButton);
		logInButton.addEventListener("click", this._OnLogInButtonClicked.bind(this));
	}

	_BindFlowToggle() {
		const sendCodeFlowToggle = DomUtils.GetElement(LoginConstants.sendCodeFlowToggleButton);
		const loginFlowToggle = DomUtils.GetElement(LoginConstants.loginFlowToggleButton);

		sendCodeFlowToggle.addEventListener("click", () => this._OnFlowToggleClicked(true));
		loginFlowToggle.addEventListener("click", () => this._OnFlowToggleClicked(false));
	}

	_BindCodeFlowModes() {
		const codeFlowEmailContainer = DomUtils.GetElement(LoginConstants.codeFlowEmailContainer);
		const codeFlowTextContainer = DomUtils.GetElement(LoginConstants.codeFlowTextContainer);
		const codeFlowEmailElement = DomUtils.GetElement(LoginConstants.codeFlowEmail);
		const codeFlowTextElement = DomUtils.GetElement(LoginConstants.codeFlowText);

		codeFlowEmailContainer.addEventListener("click", () => this._OnCodeFlowModeClicked(true));
		codeFlowEmailElement.addEventListener("click", () => this._OnCodeFlowModeClicked(true));

		codeFlowTextContainer.addEventListener("click", () => this._hasSMSConfigured === true && this._OnCodeFlowModeClicked(false));
		codeFlowTextElement.addEventListener("click", () => this._hasSMSConfigured === true && this._OnCodeFlowModeClicked(false));
	}

	_BindSendCodeButton() {
		const sendCodeButton = DomUtils.GetElement(LoginConstants.sendCodeButton);
		sendCodeButton.addEventListener("click", () => this._OnSendCodeButtonClicked());
	}

	_BindEnterKey() {
		DomUtils.OnEnter(LoginConstants.password, () => this._OnLogInButtonClicked());
		DomUtils.OnEnter(LoginConstants.codeFlowEmail, () => this._OnSendCodeButtonClicked());
		DomUtils.OnEnter(LoginConstants.codeFlowText, () => this._OnSendCodeButtonClicked());
	}

	_OnLogInButtonClicked() {
		if (this._ValidateLoginPassword()) {
			this._SubmitForm();
		}
	}

	_OnFlowToggleClicked(isSendCodeFlow) {
		this._ToggleFlow(isSendCodeFlow);
	}

	_OnExternalProviderClicked(providerType) {
		const provider = this._externalProviders?.find((p) => p.Type === providerType);
		if (provider != null) {
			window.location.href = provider.Href;
		}
	}

	_OnCodeFlowModeClicked(isCodeFlowEmail) {
		this._ToggleCodeFlowMode(isCodeFlowEmail);
	}

	_OnSendCodeButtonClicked() {
		if (this._ValidateCodeFlow()) {
			const codeFlowUsernameElement = DomUtils.GetElement(LoginConstants.codeFlowUsername);
			const codeFlowEmailElement = DomUtils.GetElement(LoginConstants.codeFlowEmail);
			const codeFlowTextElement = DomUtils.GetElement(LoginConstants.codeFlowText);

			const codeFlowEmail = codeFlowEmailElement.value;
			const codeFlowText = codeFlowTextElement.value;

			codeFlowUsernameElement.value = this._isCodeFlowEmail ? codeFlowEmail : codeFlowText;

			this._GetRecaptchaToken()
				.then((token) => {
					const codeFlowRecaptchaElement = DomUtils.GetElement(LoginConstants.codeFlowRecaptcha);
					codeFlowRecaptchaElement.value = token;

					this._SubmitForm();
				})
				.catch((error) => {
					this._ShowMessage("ERR_CODE_RECAPTCHA");
					console.warn(error);
				});
		}
	}

	/* istanbul ignore next */
	_SubmitForm() {
		super._SubmitForm(LoginConstants.loginForm);
	}

	_ToggleCodeFlowMode(isCodeFlowEmail, setUsername = false) {
		this._isCodeFlowEmail = isCodeFlowEmail;

		const codeFlowModeRadioEmailElement = DomUtils.GetElement(LoginConstants.codeFlowModeRadioEmail);
		const codeFlowEmailElement = DomUtils.GetElement(LoginConstants.codeFlowEmail);
		const codeFlowModeRadioTextElement = DomUtils.GetElement(LoginConstants.codeFlowModeRadioText);
		const codeFlowTextElement = DomUtils.GetElement(LoginConstants.codeFlowText);

		if (this._hasSMSConfigured === false) {
			this._isCodeFlowEmail = true;

			codeFlowModeRadioTextElement.disabled = true;
			codeFlowTextElement.disabled = true;

			const codeFlowTextContainer = DomUtils.GetElement(LoginConstants.codeFlowTextContainer);
			codeFlowTextContainer.style.display = "none";
			codeFlowModeRadioEmailElement.style.display = "none";
		}

		codeFlowModeRadioEmailElement.checked = this._isCodeFlowEmail;
		codeFlowModeRadioTextElement.checked = !this._isCodeFlowEmail;

		codeFlowEmailElement.classList.toggle("inactive", !this._isCodeFlowEmail);
		codeFlowTextElement.classList.toggle("inactive", this._isCodeFlowEmail);

		if (this._isSendCodeFlow === true) {
			if (this._isCodeFlowEmail) {
				DomUtils.FocusElement(codeFlowEmailElement);
				setUsername === true && (codeFlowEmailElement.value = this._codeFlowUserName);
			}
			else {
				DomUtils.FocusElement(codeFlowTextElement);
				setUsername === true && (codeFlowTextElement.value = this._codeFlowUserName);
			}
		}
	}

	_ToggleFlow(isSendCodeFlow) {
		this._isSendCodeFlow = isSendCodeFlow;

		const loginDescriptionElement = DomUtils.GetElement(LoginConstants.loginDescription);

		const loginFlowContainer = DomUtils.GetElement(LoginConstants.loginFlowContainer);
		const sendCodeFlowContainer = DomUtils.GetElement(LoginConstants.sendCodeFlowContainer);

		const allLoginInputs = loginFlowContainer.querySelectorAll("input");
		const allSendCodeInputs = sendCodeFlowContainer.querySelectorAll("input");

		allLoginInputs.forEach((i) => (i.disabled = this._isSendCodeFlow));
		allSendCodeInputs.forEach((i) => (i.disabled = !this._isSendCodeFlow));

		if (this._isSendCodeFlow) {
			loginFlowContainer.style.display = "none";
			sendCodeFlowContainer.style.display = "block";

			this._localeService.SetElementTranslation(loginDescriptionElement, "oauth.sendOneTimeCodeDescription");

			this._ToggleCodeFlowMode(this._isCodeFlowEmail);
		}
		else {
			loginFlowContainer.style.display = "block";
			sendCodeFlowContainer.style.display = "none";

			this._localeService.SetElementTranslation(loginDescriptionElement, "oauth.verifyDescription");

			DomUtils.FocusElement(LoginConstants.username);
		}

		const authenticationFlowTypeElement = DomUtils.GetElement(LoginConstants.authenticationFlowType);
		authenticationFlowTypeElement.value = this._isSendCodeFlow ? AuthenticationFlowType.SendCode : AuthenticationFlowType.Login;
	}

	_ToggleSendCodeContainer(isAuthCodeEnabled) {
		const sendCodeContainer = DomUtils.GetElement(LoginConstants.sendCodeToggleContainer);
		sendCodeContainer.style.display = isAuthCodeEnabled ? "block" : "none";
	}

	_SetupExternalProviders(providers) {
		const hasProviders = providers != null && providers.length > 0;

		DomUtils.ShowHide(hasProviders, LoginConstants.externalProvidersOrSignInWith);

		const externalProvidersContainerElement = DomUtils.GetElement(LoginConstants.externalProvidersContainer);
		DomUtils.ShowHide(hasProviders, externalProvidersContainerElement);

		if (hasProviders === true) {
			ExternalProviders.Init(externalProvidersContainerElement, providers, (providerType) =>
				this._OnExternalProviderClicked(providerType)
			);
		}
	}

	_SetAccountLinks() {
		const forgotPasswordLink = DomUtils.GetElement(LoginConstants.forgotPassword);
		const signUpLink = DomUtils.GetElement(LoginConstants.signUp);

		if (this._forgotPasswordLink != null) {
			forgotPasswordLink.href = this._forgotPasswordLink;
		}
		else {
			forgotPasswordLink.style.display = "none";
		}

		if (this._signUpLink != null) {
			signUpLink.href = this._signUpLink;
		}
		else {
			signUpLink.style.display = "none";
		}
	}

	_ValidateLoginPassword() {
		const usernameElement = DomUtils.GetElement(LoginConstants.username);
		const passwordElement = DomUtils.GetElement(LoginConstants.password);

		const usernameValid = usernameElement.validate() == null;
		const passwordValid = passwordElement.validate() == null;

		return usernameValid && passwordValid;
	}

	_ValidateCodeFlow() {
		if (this._isCodeFlowEmail) {
			return this._ValidateCodeFlowEmail();
		}
		else {
			return this._ValidateCodeFlowText();
		}
	}

	_ValidateCodeFlowEmail() {
		const codeFlowEmailElement = DomUtils.GetElement(LoginConstants.codeFlowEmail);
		return codeFlowEmailElement.validate() == null;
	}

	_ValidateCodeFlowText() {
		const codeFlowTextElement = DomUtils.GetElement(LoginConstants.codeFlowText);
		return codeFlowTextElement.validate() == null;
	}

	_ToggleRememberMe(checked) {
		const rememberMeElements = document.querySelectorAll(`.${LoginConstants.rememberMeCheckClass}`);
		rememberMeElements.forEach((elem) => {
			elem.checked = checked;
		});
	}
}
